'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowRight,
  Zap,
  TrendingUp,
  ChevronDown,
  ShieldCheck,
} from 'lucide-react';
import Link from 'next/link';
import { servicesService } from '@/lib/services';
import { sortServicesForNavigation } from '@/lib/serviceOrdering';

interface HeroSectionProps {
  title?: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  heroImage?: string;
  heroImageAlt?: string;
}

export default function HeroSection({
  title = 'Transform your marketing with AI',
  subtitle = "We build intelligent systems that amplify your team's capabilities. Data-driven decisions, automated workflows, measurable results.",
  ctaText = 'Start Your Transformation',
  ctaLink = '#contact',
  heroImage = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop',
  heroImageAlt = 'Marketing analytics dashboard',
}: HeroSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [services, setServices] = useState<Array<{ title: string; slug: string }>>([]);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesDropdownRef = useRef<HTMLDivElement | null>(null);
  const servicesButtonRef = useRef<HTMLButtonElement | null>(null);
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
  const portalDropdownRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const imageParallaxRef = useRef<HTMLDivElement | null>(null);
  const blobARef = useRef<HTMLDivElement | null>(null);
  const blobBRef = useRef<HTMLDivElement | null>(null);
  const blobCRef = useRef<HTMLDivElement | null>(null);
  const reducedMotionRef = useRef(false);

  const headingParts = useMemo(() => {
    const words = title.trim().split(/\s+/).filter(Boolean);
    if (words.length < 3) {
      return {
        lead: title,
        accent: 'Real Growth.',
      };
    }

    const accentWords = words.slice(-2).join(' ');
    const leadWords = words.slice(0, -2).join(' ');

    return {
      lead: leadWords,
      accent: `${accentWords}.`.replace(/\.{2,}/g, '.'),
    };
  }, [title]);

  useEffect(() => {
    setIsHydrated(true);
    servicesService.getActive().then((data) => {
      const mapped = data.map((s) => ({ title: s.title, slug: s.slug }));
      setServices(sortServicesForNavigation(mapped));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!servicesOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideButton = servicesDropdownRef.current?.contains(target);
      const insidePortal = portalDropdownRef.current?.contains(target);
      if (!insideButton && !insidePortal) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [servicesOpen]);

  useEffect(() => {
    if (!isHydrated) return;

    try {
      reducedMotionRef.current =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      reducedMotionRef.current = false;
    }

    if (reducedMotionRef.current) return;

    const el = sectionRef.current;
    if (!el) return;

    const handleMove = (e: MouseEvent) => {
      if (window.innerWidth < 768) return;

      const rect = el.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / rect.width - 0.5;
      const ny = (e.clientY - rect.top) / rect.height - 0.5;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const img = imageParallaxRef.current;
        const a = blobARef.current;
        const b = blobBRef.current;
        const c = blobCRef.current;

        if (img) {
          img.style.transform = `translate3d(${nx * 12}px, ${ny * 12}px, 0) rotate(${nx * 2}deg)`;
        }
        if (a) {
          a.style.transform = `translate3d(${nx * -20}px, ${ny * -16}px, 0) scale(${1 + Math.abs(nx) * 0.1})`;
        }
        if (b) {
          b.style.transform = `translate3d(${nx * 25}px, ${ny * 18}px, 0) scale(${1 + Math.abs(ny) * 0.1})`;
        }
        if (c) {
          c.style.transform = `translate3d(${nx * 16}px, ${ny * -12}px, 0) rotate(${nx * -20}deg)`;
        }
      });
    };

    const handleLeave = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        if (imageParallaxRef.current)
          imageParallaxRef.current.style.transform = 'translate3d(0,0,0) rotate(0deg)';
        if (blobARef.current) blobARef.current.style.transform = 'translate3d(0,0,0) scale(1)';
        if (blobBRef.current) blobBRef.current.style.transform = 'translate3d(0,0,0) scale(1)';
        if (blobCRef.current) blobCRef.current.style.transform = 'translate3d(0,0,0) rotate(0deg)';
      });
    };

    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);

    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isHydrated]);

  const handleCTAClick = () => {
    if (ctaLink.startsWith('#')) {
      const element = document.getElementById(ctaLink.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = ctaLink;
    }
  };

  const handleDiscoverMoreClick = () => {
    if (typeof window === 'undefined') return;
    window.scrollBy({
      top: Math.round(window.innerHeight * 0.35),
      behavior: 'smooth',
    });
  };

  return (
    <section
      ref={sectionRef}
      className="relative isolate z-20 bg-gradient-to-br from-rose-50 via-white to-sky-50 min-h-screen flex items-center justify-center pt-28 sm:pt-28 md:pt-12 lg:pt-14 overflow-x-hidden"
    >
      {/* Background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          ref={blobARef}
          className="absolute -top-16 -left-16 h-[500px] w-[500px] rounded-full bg-pink-300/35 blur-[100px] transition-all duration-700 ease-out"
          style={{ animation: 'float-soft 10s ease-in-out infinite' }}
        />
        <div
          ref={blobBRef}
          className="absolute -top-8 -right-20 h-[580px] w-[580px] rounded-full bg-blue-300/30 blur-[110px] transition-all duration-700 ease-out"
          style={{ animation: 'float 6s ease-in-out infinite', animationDelay: '900ms' }}
        />
        <div
          ref={blobCRef}
          className="absolute -bottom-24 left-1/3 h-[480px] w-[480px] rounded-full bg-violet-300/25 blur-[100px] transition-all duration-700 ease-out"
          style={{ animation: 'drift-x 14s ease-in-out infinite' }}
        />

        {/* Floating particles */}
        {[
          { left: '9%', top: '12%', size: 4, delay: '0s', color: 'bg-pink-400' },
          { left: '14%', top: '38%', size: 3, delay: '0.6s', color: 'bg-blue-400' },
          { left: '84%', top: '18%', size: 4, delay: '1.2s', color: 'bg-emerald-400' },
          { left: '80%', top: '70%', size: 3, delay: '0.9s', color: 'bg-pink-400' },
          { left: '7%', top: '64%', size: 4, delay: '1.5s', color: 'bg-blue-400' },
          { left: '91%', top: '44%', size: 3, delay: '0.3s', color: 'bg-violet-400' },
        ].map((p, i) => (
          <div
            key={i}
            className={`absolute ${p.color}/50 rounded-full`}
            style={{
              left: p.left,
              top: p.top,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animation:
                i % 2 === 0
                  ? `float ${5 + i}s ease-in-out infinite`
                  : `float-soft ${6 + i}s ease-in-out infinite`,
              animationDelay: p.delay,
            }}
          />
        ))}
      </div>

      <div className="w-full relative z-10 px-4 sm:px-6 lg:px-8 xl:px-10 py-12 md:py-14 lg:py-20">
        <div className="grid items-center gap-10 lg:gap-16 lg:grid-cols-2">
          {/* ── Left: Text ── */}
          <div className="min-w-0 text-center lg:text-left space-y-6 md:space-y-7">
            {/* Badge */}
            <div className="inline-flex items-center gap-2.5 rounded-full border border-pink-200 bg-white/80 px-4 py-2 backdrop-blur-md shadow-sm animate-slide-down">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-pink-400 opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-pink-500" />
              </span>
              <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.22em] text-pink-600">
                AI-Led Marketing Systems
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.04] tracking-tight animate-slide-down text-balance">
              <span className="text-slate-900">{headingParts.lead}</span>
              <span className="block mt-2 bg-gradient-to-r from-pink-500 via-violet-500 to-blue-500 bg-[length:200%_100%] bg-clip-text text-transparent animate-gradient-pan pb-2">
                {headingParts.accent}
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-base sm:text-[1.05rem] text-slate-500 leading-relaxed max-w-xl mx-auto lg:mx-0 animate-slide-up">
              {subtitle}
            </p>

            {/* Trust pills */}
            {/* <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 animate-slide-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-1.5 text-[11px] font-bold text-slate-600 shadow-sm backdrop-blur-sm">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                500+ projects delivered
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-1.5 text-[11px] font-bold text-slate-600 shadow-sm backdrop-blur-sm">
                <TrendingUp className="w-3.5 h-3.5 text-blue-500" />
                50+ countries served
              </div>
            </div> */}

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-slide-up">
              <button
                onClick={handleCTAClick}
                className="group relative px-7 py-3.5 rounded-2xl font-bold text-sm sm:text-base flex items-center justify-center gap-3 overflow-hidden text-white shadow-[0_8px_30px_rgba(236,72,153,0.4)] hover:shadow-[0_12px_40px_rgba(236,72,153,0.55)] transition-all duration-300 hover:-translate-y-1 active:scale-95"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-rose-500" />
                <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-violet-500 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="relative z-10">{ctaText}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300 relative z-10" />
              </button>
              <div
                ref={servicesDropdownRef}
                className="relative hidden 2xl:block"
                onMouseEnter={() => {
                  if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
                  if (servicesButtonRef.current) {
                    setDropdownRect(servicesButtonRef.current.getBoundingClientRect());
                  }
                  setServicesOpen(true);
                }}
                onMouseLeave={() => {
                  closeTimerRef.current = setTimeout(() => setServicesOpen(false), 150);
                }}
              >
                <button
                  ref={servicesButtonRef}
                  type="button"
                  onClick={() => {
                    if (servicesButtonRef.current) {
                      setDropdownRect(servicesButtonRef.current.getBoundingClientRect());
                    }
                    setServicesOpen((o) => !o);
                  }}
                  className="group w-full px-6 py-3.5 rounded-2xl font-bold text-sm sm:text-base flex items-center justify-center gap-3 text-slate-700 border-2 border-slate-200 bg-white/80 hover:bg-white hover:border-slate-300 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 active:scale-95"
                >
                  <span>Explore Services</span>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                </button>

                {servicesOpen && services.length > 0 && dropdownRect && createPortal(
                  <div
                    ref={portalDropdownRef}
                    style={{
                      position: 'fixed',
                      top: dropdownRect.bottom + 8,
                      left: dropdownRect.left,
                      width: Math.max(dropdownRect.width, 220),
                      zIndex: 99999,
                    }}
                    className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-2xl shadow-2xl max-h-64 overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#ec4899_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-pink-400"
                    onMouseEnter={() => {
                      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
                    }}
                    onMouseLeave={() => {
                      closeTimerRef.current = setTimeout(() => setServicesOpen(false), 150);
                    }}
                  >
                    {services.map((service) => (
                      <Link
                        key={service.slug}
                        href={`/services/${service.slug}`}
                        onClick={() => setServicesOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-primary transition-colors"
                      >
                        {service.title}
                      </Link>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
            </div>

          </div>

          {/* ── Right: Image ── */}
          <div className="relative mx-auto w-full animate-fade-in lg:max-w-2xl group/hero">

            {/* Outer div: receives parallax transform — NO overflow-hidden here */}
            <div
              ref={imageParallaxRef}
              className="relative will-change-transform aspect-[4/3]"
            >
            {/* Inner div: absolute inset-0 fills parent reliably, owns clip + radius */}
            <div className="absolute inset-0 rounded-[2.4rem] overflow-hidden isolate">
              <img
                src={heroImage}
                alt={heroImageAlt}
                className="w-full h-full object-cover transform scale-[1.02] group-hover/hero:scale-105 transition-transform duration-[5s] ease-out"
              />
              {/* <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/12 via-transparent to-blue-500/12 z-10" /> */}

                {/* Floating cards */}
                <div className="pointer-events-none absolute inset-0 hidden sm:block z-20">
                  <div className="absolute left-6 top-6 rounded-2xl bg-white/95 backdrop-blur-xl p-3.5 shadow-xl border border-white/60 animate-float">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-md shadow-pink-200">
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-black text-slate-900">Automation-first</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          Efficiency redefined
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="absolute right-6 bottom-16 rounded-2xl bg-white/95 backdrop-blur-xl p-3.5 shadow-xl border border-white/60 animate-float-soft"
                    style={{ animationDelay: '700ms' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-md shadow-blue-200">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-black text-slate-900">Real Growth</div>
                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                          Measurable impact
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="absolute left-8 bottom-6 rounded-2xl bg-white/95 backdrop-blur-xl p-3.5 shadow-xl border border-white/60 animate-float"
                    style={{ animationDelay: '1.1s' }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold mb-2">
                      Performance Lift
                    </div>
                    <div className="flex items-end gap-1.5 h-10">
                      {[38, 44, 52, 63, 74, 89].map((bar, index) => (
                        <span
                          key={bar}
                          className="w-2 rounded-full bg-gradient-to-t from-pink-500 to-violet-400"
                          style={{
                            height: `${bar}%`,
                            animation: `slide-up 400ms ease-out ${index * 80}ms both`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* end imageParallaxRef outer */}

            {/* Discover more (mobile) */}
            <button
              type="button"
              onClick={handleDiscoverMoreClick}
              className="mt-6 w-full flex flex-col items-center justify-center text-center gap-2 text-slate-400 lg:hidden transition-colors hover:text-slate-500"
              aria-label="Discover more content"
            >
              <span className="text-[9px] font-black uppercase tracking-[0.3em] opacity-70">
                Discover More
              </span>
              <ChevronDown className="w-4 h-4 animate-bounce opacity-70" />
            </button>
          </div>
        </div>

        {/* Full-width service chips strip */}
        {services.length > 0 && (
          <div className="2xl:hidden w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden animate-slide-up mt-8">
            <div className="flex gap-2 pb-0.5" style={{ width: 'max-content' }}>
              {services.map((service) => (
                <Link
                  key={service.slug}
                  href={`/services/${service.slug}`}
                  className="flex-none px-4 py-2 rounded-full text-xs font-semibold text-slate-700 border border-slate-400 bg-white/80 backdrop-blur-sm shadow-sm active:bg-white active:border-slate-500 transition-all duration-200 whitespace-nowrap"
                >
                  {service.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
